import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { Box, Button, Header, Modal, Pagination, SpaceBetween, Table } from '@amzn/awsui-components-react-v3';

import { useMetrics } from 'appSelectorContext';
import { uniqueId } from 'lodash';

import { METRIC_PUBLISHER_NAMES } from '../../constants';
import { Model, MPNToIPNPair, ReceiveType, URIParams } from '../../store/types';
import { getOrderLineItemNumber, getOrderNumber } from '../Utils/Utils';
import { MPN_IPN_SELECTED_ASN } from './constants';

interface MultipleIPNModelsProps {
  models: Model[];
  onDismiss?: () => void;
  onConfirm?: (model: Model) => void;
  mpnMappingSelected: MPNToIPNPair | null;
  isPo?: Boolean;
}

const sortByOrder = (a: Model, b: Model) => {
  /* istanbul ignore next */
  if (!a.orderNumber) {
    return 1;
  }
  if (!b.orderNumber) {
    return -1;
  }

  return a.orderNumber > b.orderNumber ? -1 : 1;
};

export const MultipleIPNModels = ({ models, onDismiss, onConfirm, isPo }: MultipleIPNModelsProps) => {
  const [visible, setVisible] = useState(true);
  const [selectedItems, setSelectedItems] = React.useState<Model[]>([]);
  const context = useMetrics();
  const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(METRIC_PUBLISHER_NAMES.SCAN_MPN);
  const { receiveType } = useParams<URIParams>();
  const isRMA = receiveType === 'no_reference';
  const isAdjustment = receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  const selectableId = isPo || isRMA || isAdjustment;
  const mappedModels = useMemo(
    () =>
      models
        .map((model) => {
          const orderNumber = getOrderNumber({ model });
          const orderLineItemNumber = getOrderLineItemNumber({ model });
          const trackingId = uniqueId();
          return { ...model, orderNumber, orderLineItemNumber, trackingId };
        })
        .sort(sortByOrder),
    [models],
  );

  /* istanbul ignore next */
  const ColumnDefinitions = [
    {
      id: 'trackingId',
      header: 'Tracking ID',
      /* istanbul ignore next */
      cell: (e: Model) => e?.trackingId,
    },
    {
      id: 'referenceLineItemId',
      header: 'Reference Line Item ID',
      /* istanbul ignore next */
      cell: (e: Model) => e?.referenceLineItemId,
    },
    {
      id: 'orderNumber',
      header: 'Order number',
      cell: (e: Model) => e.orderNumber,
    },
    {
      id: 'orderedIPN',
      header: 'Ordered IPN',
      cell: (e: Model) => e.orderedIPN,
    },
    {
      id: 'expectedIPN',
      header: 'Expected IPN',
      cell: (e: Model) => e.expectedIPN,
    },
    {
      id: 'expectedQty',
      header: 'Expected Qty',
      cell: (e: Model) => e.maximumExpectedQuantity,
    },
    {
      id: 'receivedQty',
      header: 'Received Qty',
      cell: (e: Model) => e.currentReceivedQuantity,
    },
    {
      id: 'orderLineItemNumber',
      header: 'Order line item number',
      cell: (e: Model) => e.orderLineItemNumber,
    },
    {
      id: 'description',
      header: 'Amazon part number description',
      cell: (e: Model) => e.description,
    },
  ];

  // pagination props
  const { items, paginationProps } = useCollection(mappedModels, {
    pagination: { pageSize: 25 },
    sorting: {},
  });

  return (
    <Modal
      onDismiss={() => {
        setVisible(false);
        onDismiss && onDismiss();
      }}
      visible={visible}
      size="large"
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              ariaLabel="Cancel IPN Selection"
              onClick={() => {
                setVisible(false);
                onDismiss && onDismiss();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              ariaLabel="Confirm selected IPN"
              disabled={selectedItems.length === 0}
              onClick={() => {
                setVisible(false);
                onConfirm && selectedItems?.length > 0 && onConfirm(selectedItems[0]);
                publisher.publishCounterMonitor(MPN_IPN_SELECTED_ASN, 1);
              }}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={
        <Header
          variant="h2"
          actions={null}
          description="Choose internal part number (IPN) from the packing slip, case or component that corresponds to right manufacturing part number (MPN) and/or compare against APN Description."
        >
          Choose the valid line for the MPN - {models?.[0]?.expectedMPN} being received
        </Header>
      }
    >
      <Table
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        selectedItems={selectedItems}
        ariaLabels={{
          selectionGroupLabel: 'Items selection',
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter((i) => {
              if (!selectableId) {
                return i.referenceLineItemId === item.referenceLineItemId;
              }
              return i.trackingId === item.trackingId;
            }).length;
            const id = selectableId ? item.trackingId : item.referenceLineItemId;
            return `${id} is ${isItemSelected ? '' : 'not'} selected`;
          },
        }}
        columnDefinitions={ColumnDefinitions}
        onRowClick={({ detail }: any) => {
          setSelectedItems([detail.item]);
        }}
        pagination={<Pagination {...paginationProps} />}
        items={items}
        loadingText="Loading resources"
        selectionType="single"
        trackBy={selectableId ? 'trackingId' : 'referenceLineItemId'}
        visibleColumns={
          selectableId
            ? ['orderedIPN', 'expectedIPN', 'expectedQty', 'receivedQty', 'description', 'orderLineItemNumber']
            : ['orderNumber', 'expectedIPN', 'expectedQty', 'receivedQty', 'description', 'orderLineItemNumber']
        }
      />
    </Modal>
  );
};
